import PageHeader from "@/components/site/PageHeader";

const SECTIONS = [
    {
        eyebrow: "The Approach",
        title: "A more granular approach to medical distribution.",
        paragraphs: [
            "For more than 20 years, people have predicted the demise of the medical sales representative.",
            "We don't see it that way.",
            "Technology has changed. Hospitals have consolidated. Purchasing has become more sophisticated. Manufacturers have more ways than ever to reach physicians directly.",
            "But relationships still matter.",
            "Great medical sales representatives provide real value to their surgeon customers. They understand their practices, know their hospitals, help navigate complex approval processes, support new technology, and — most importantly — have earned trust over years of working together.",
            "1099er was built around that relationship.",
        ],
    },
    {
        eyebrow: "The Model",
        title: "Distribution doesn't have to mean a territory.",
        paragraphs: [
            "The traditional medical device distribution model is largely geographic.",
            "A company looking to enter a new market might hire a direct sales force, appoint a distributor for an entire state or metropolitan area, or sign a large national distribution partner.",
            "Those models work.",
            "But they aren't the only way.",
            "A company may not need another distributor covering all of Denver. It may need the right representative with relationships with five specific surgeons in Denver.",
            "That's where 1099er gets granular.",
            "We connect medical technology companies with experienced representatives who already have relationships in the specialties, accounts, and physician communities those companies are trying to reach.",
        ],
        callout: "Not just the right territory. The right relationship.",
    },
    {
        eyebrow: "Why It Works",
        title: "A different tool for growing market share.",
        paragraphs: [
            "1099er isn't designed to replace traditional distribution.",
            "It's another tool.",
            "For an emerging company, it can provide a way to enter markets without immediately building an expensive direct sales organization or awarding large territories.",
            "For an established company, it can help fill gaps, reach underpenetrated accounts, introduce a specialty product, or pursue individual physician opportunities that may not justify adding another full-time representative.",
            "For the rep, it creates an opportunity to represent differentiated, complementary technologies that make sense for the physicians they already serve.",
            "The result is a more targeted approach to distribution:",
        ],
        pillars: [
            { k: "01", label: "The right product." },
            { k: "02", label: "The right rep." },
            { k: "03", label: "The right physician." },
        ],
    },
    {
        eyebrow: "How We Think",
        title: "Built for the way medical sales actually works.",
        paragraphs: [
            "A map can tell you who covers a territory.",
            "It can't tell you who a surgeon trusts.",
            "That's the difference we're interested in.",
            "1099er is building a network around the relationships that already exist throughout the medical sales community and connecting those relationships with companies looking for smarter ways to grow.",
            "Sometimes that could mean an entire market.",
            "Sometimes it could mean a health system.",
            "Sometimes it could mean a single surgeon.",
            "That's the point. Get granular.",
        ],
    },
    {
        eyebrow: "The Belief",
        title: "Relationships still matter.",
        paragraphs: [
            "We believe good representatives aren't going away.",
            "Their role may change. The products may change. The way companies deploy sales resources may change.",
            "But when a knowledgeable rep understands a technology, believes it can help their customer, and has the relationship necessary to introduce it, that rep remains one of the most valuable resources a medical technology company can have.",
            "1099er gives companies a new way to find and leverage that resource.",
        ],
        callout: "Better relationships. Smarter distribution. More granular growth.",
    },
];

export default function AboutPage() {
    return (
        <div data-testid="about-page">
            <PageHeader
                eyebrow="About 1099er"
                title="Better relationships. Smarter distribution."
                description="1099er is a private network connecting medical technology companies with experienced sales representatives who already have the specialty, account, and physician relationships those companies are trying to reach."
                testId="about-header"
            />

            <div data-testid="about-sections">
                {SECTIONS.map((s, idx) => {
                    const bg = idx % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]";
                    return (
                        <section
                            key={s.title}
                            data-testid={`about-section-${idx + 1}`}
                            className={`${bg} border-b border-slate-200`}
                        >
                            <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
                                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                                    {s.eyebrow}
                                </p>
                                <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                                    {s.title}
                                </h2>

                                <div className="mt-8 space-y-5 text-[15px] md:text-lg text-slate-600 leading-relaxed">
                                    {s.paragraphs.map((p, i) => (
                                        <p key={i}>{p}</p>
                                    ))}
                                </div>

                                {s.pillars && (
                                    <ul
                                        data-testid={`about-section-${idx + 1}-pillars`}
                                        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3"
                                    >
                                        {s.pillars.map((p) => (
                                            <li
                                                key={p.k}
                                                className="rounded-xl border border-slate-200 bg-white p-5"
                                            >
                                                <p className="text-xs font-mono tracking-widest text-slate-400">
                                                    {p.k}
                                                </p>
                                                <p className="mt-2 font-display text-lg font-semibold text-slate-900">
                                                    {p.label}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {s.callout && (
                                    <p
                                        data-testid={`about-section-${idx + 1}-callout`}
                                        className="mt-10 border-l-2 border-slate-900 pl-5 font-display text-xl md:text-2xl text-slate-900 leading-snug"
                                    >
                                        {s.callout}
                                    </p>
                                )}
                            </div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
}
